import { addLearningContentToLevels } from './learningContent';

const baseLevels = [
  {
    id: 1,
    title: "Hello World",
    description: "Print 'Hello, World!' to the console.",
    starterCode: '# Write your code here\n',
    tests: [{ input: "", expected: "Hello, World!" }],
    explanation: "The print() function outputs text to the console.",
    hint: "Use print('Hello, World!')",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced making a computer communicate to humans. Writing once so others can see—this is how information spreads without repetition."
    }
  },
  {
    id: 2,
    title: "Variables",
    description: "Create a variable name = 'Sifiso' and print it.",
    starterCode: "",
    tests: [{ input: "", expected: "Sifiso" }],
    explanation: "Variables store data that can be used and changed throughout your program.",
    hint: "name = 'Sifiso'\nprint(name)",
    pillar: {
      primary: 'digital',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced storing value for later use. Like a grocery tin that holds money between payday and shopping day, variables hold information between one part of your program and another."
    }
  },
  {
    id: 3,
    title: "Data Types",
    description: "Print the type of 42, 'text', and True.",
    starterCode: "",
    tests: [{ input: "", expected: "<class 'int'>\n<class 'str'>\n<class 'bool'>" }],
    explanation: "Python has built-in data types such as integers (int), strings (str), and booleans (bool). Use type() to inspect them.",
    hint: "Use type()",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced recognizing that different things need different handling. A computer doesn't guess what kind of information you're working with—it categorizes and remembers."
    }
  },
  {
    id: 4,
    title: "Arithmetic",
    description: "Compute (10 + 5) * 2 and print the result.",
    starterCode: "",
    tests: [{ input: "", expected: "30" }],
    explanation: "Python supports standard arithmetic operators: +, -, *, / (float division), // (floor division), and % (modulo). Parentheses control order of operations.",
    hint: "print((10 + 5) * 2)",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced two-way conversation with a machine. Ask, receive, respond—this is how systems gather what they need and give back what you want."
    }
  },
  {
    id: 5,
    title: "Strings",
    description: "Concatenate 'Hello' and 'World' with a space.",
    starterCode: "",
    tests: [{ input: "", expected: "Hello World" }],
    explanation: "Strings can be joined using the + operator or more elegantly with f-strings (formatted string literals).",
    hint: "Use + or f-strings",
    pillar: {
      primary: 'economic',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'economic',
      text: "You practiced calculation for fairness and verification. Whether splitting costs or making change, math protects you from mistakes and ensures everyone gets what they're owed."
    }
  },
  {
    id: 6,
    title: "Conditionals (if/else)",
    description: "Write a program that checks if a number is positive or negative. Test with -5.",
    starterCode: "number = -5\n# Write your if/else statement here\n",
    tests: [
      { input: "", expected: "Negative" }
    ],
    explanation: "Conditionals let your code make decisions using if and else statements.",
    hint: "Use if number >= 0: ... else: ...",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced looking at reality and choosing actions based on what's true. Decisions aren't guesses—they're responses to conditions you can see."
    }
  },
  {
    id: 7,
    title: "Multiple Conditions (elif)",
    description: "Create a grade classifier: 90+ = A, 80-89 = B, 70-79 = C, below 70 = F. Test with score 85.",
    starterCode: "score = 85\n# Write your if/elif/else chain\n",
    tests: [
      { input: "", expected: "B" }
    ],
    explanation: "Use elif to check multiple conditions in order until one matches.",
    hint: "if score >= 90: ... elif score >= 80: ...",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced handling situations where different cases need different responses. Not everything fits into yes or no—sometimes the answer depends on which situation you're in."
    }
  },
  {
    id: 8,
    title: "Boolean Logic (and, or, not)",
    description: "Check if a person can vote: age >= 18 AND is_citizen = True. Test with age=20, is_citizen=True.",
    starterCode: "age = 20\nis_citizen = True\n# Check both conditions\n",
    tests: [
      { input: "", expected: "Can vote" }
    ],
    explanation: "Boolean operators combine conditions: and (both true), or (at least one true), not (inverts).",
    hint: "if age >= 18 and is_citizen:",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced combining conditions to make better decisions. When two things must both be true, you're protecting yourself from incomplete information."
    }
  },
  {
    id: 9,
    title: "Comparison Operators",
    description: "Compare two numbers and print which is larger, or if they're equal. Test with a=10, b=5.",
    starterCode: "a = 10\nb = 5\n",
    tests: [
      { input: "", expected: "a is larger" }
    ],
    explanation: "Comparison operators: == (equal), != (not equal), < > <= >= (ordering).",
    hint: "if a > b: print('a is larger')",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced evaluation and comparison to make choices. Knowing whether something is more, less, or equal lets you respond to competition and opportunity."
    }
  },
  {
    id: 10,
    title: "Nested Conditionals",
    description: "Check ticket price: if age < 12 'Child $5', elif age < 65 check if student 'Student $8' else 'Adult $12', else 'Senior $6'. Test: age=25, student=True.",
    starterCode: "age = 25\nstudent = True\n",
    tests: [
      { input: "", expected: "Student $8" }
    ],
    explanation: "Nested conditionals put if statements inside other if statements for complex logic.",
    hint: "if age < 12: ... elif age < 65: if student: ...",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced decisions within decisions. Complex situations require layered thinking—first check one thing, then based on that, check something else."
    }
  },
  {
    id: 11,
    title: "While Loops",
    description: "Print numbers 1 through 5 using a while loop.",
    starterCode: "# Initialize counter and use while loop\n",
    tests: [
      { input: "", expected: "1\n2\n3\n4\n5" }
    ],
    explanation: "While loops repeat as long as a condition is True. Remember to update the counter!",
    hint: "i = 1; while i <= 5: print(i); i += 1",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced controlled repetition. Keep going as long as the work isn't done, then stop—this is how you process uncertainty without getting stuck."
    }
  },
  {
    id: 12,
    title: "For Loops",
    description: "Iterate through the list ['apple', 'banana', 'cherry'] and print each fruit.",
    starterCode: "fruits = ['apple', 'banana', 'cherry']\n",
    tests: [
      { input: "", expected: "apple\nbanana\ncherry" }
    ],
    explanation: "For loops iterate over sequences like lists, strings, and ranges.",
    hint: "for fruit in fruits: print(fruit)",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced systematic processing of each item in a collection. Going through things one by one, in order, ensures nothing is missed and everything gets handled."
    }
  },
  {
    id: 13,
    title: "Range Function",
    description: "Use range() to print even numbers from 0 to 10 (inclusive).",
    starterCode: "",
    tests: [
      { input: "", expected: "0\n2\n4\n6\n8\n10" }
    ],
    explanation: "range(start, stop, step) generates number sequences. range(0, 11, 2) gives even numbers.",
    hint: "for i in range(0, 11, 2): print(i)",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced generating structured sequences. When you need to count, iterate, or repeat something a specific number of times, systems don't guess—they follow a pattern."
    }
  },
  {
    id: 14,
    title: "Break and Continue",
    description: "Print numbers 1-10, but skip 5 (continue) and stop at 8 (break).",
    starterCode: "",
    tests: [
      { input: "", expected: "1\n2\n3\n4\n6\n7\n8" }
    ],
    explanation: "break exits the loop entirely, continue skips to the next iteration.",
    hint: "for i in range(1, 11): if i == 5: continue; print(i); if i == 8: break",
    pillar: {
      primary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced stopping when you have enough information and skipping what doesn't matter. This is how experienced people save time and energy in complex situations."
    }
  },
  {
    id: 15,
    title: "Nested Loops",
    description: "Print a 3x3 multiplication table (rows 1-3, columns 1-3) in format 'row x col = result'.",
    starterCode: "",
    tests: [
      { input: "", expected: "1 x 1 = 1\n1 x 2 = 2\n1 x 3 = 3\n2 x 1 = 2\n2 x 2 = 4\n2 x 3 = 6\n3 x 1 = 3\n3 x 2 = 6\n3 x 3 = 9" }
    ],
    explanation: "Nested loops place one loop inside another for working with 2D data.",
    hint: "for row in range(1, 4): for col in range(1, 4): print(f'{row} x {col} = {row*col}')",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced handling patterns within patterns. When reality repeats in multiple dimensions—like days and shops—you need systematic thinking to cover everything without confusion."
    }
  },
  {
    id: 16,
    title: "Creating and Accessing Lists",
    description: "Create a list with values [10, 20, 30, 40, 50] and print the first and last elements.",
    starterCode: "",
    tests: [
      { input: "", expected: "10\n50" }
    ],
    explanation: "Lists are ordered collections accessed by index. Index 0 is first, -1 is last.",
    hint: "my_list = [10, 20, 30, 40, 50]; print(my_list[0]); print(my_list[-1])",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced organizing related information together instead of scattering it. Memory isn't unlimited—grouping things saves effort and reduces mistakes."
    }
  },
  {
    id: 17,
    title: "List Methods (append, remove, etc.)",
    description: "Start with [1, 2, 3], append 4, remove 2, then print the list.",
    starterCode: "numbers = [1, 2, 3]\n",
    tests: [
      { input: "", expected: "[1, 3, 4]" }
    ],
    explanation: "List methods: append() adds to end, remove() deletes first occurrence, pop() removes by index.",
    hint: "numbers.append(4); numbers.remove(2); print(numbers)",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced managing information that changes over time. Lists grow, shrink, and rearrange as reality shifts—your system needs to track that."
    }
  },
  {
    id: 18,
    title: "List Slicing",
    description: "From list [0,1,2,3,4,5,6,7,8,9], print elements from index 2 to 7 (exclusive).",
    starterCode: "numbers = [0,1,2,3,4,5,6,7,8,9]\n",
    tests: [
      { input: "", expected: "[2, 3, 4, 5, 6]" }
    ],
    explanation: "Slicing extracts portions: list[start:stop:step]. Stop index is exclusive.",
    hint: "print(numbers[2:7])",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced working with just the portion you need. You don't always process everything—sometimes the first few, the last few, or a middle section is what matters."
    }
  },
  {
    id: 19,
    title: "List Comprehensions",
    description: "Create a list of squares for numbers 0-4 using list comprehension.",
    starterCode: "",
    tests: [
      { input: "", expected: "[0, 1, 4, 9, 16]" }
    ],
    explanation: "List comprehensions provide concise syntax: [expression for item in iterable].",
    hint: "squares = [x**2 for x in range(5)]; print(squares)",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced transforming entire collections efficiently. When you need to apply the same change to many items, doing it systematically saves hours of manual work."
    }
  },
  {
    id: 20,
    title: "Iterating Over Lists",
    description: "Calculate and print the sum of [10, 20, 30, 40, 50] using a for loop.",
    starterCode: "numbers = [10, 20, 30, 40, 50]\n",
    tests: [
      { input: "", expected: "150" }
    ],
    explanation: "Iterate through lists with for loops to process each element.",
    hint: "total = 0; for num in numbers: total += num; print(total)",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced processing each item methodically. One at a time, in sequence, with the same operation—this is how systems ensure consistency and completeness."
    }
  },
  {
    id: 21,
    title: "Tuples",
    description: "Create a tuple (1, 2, 3, 4, 5) and print the third element (index 2).",
    starterCode: "",
    tests: [
      { input: "", expected: "3" }
    ],
    explanation: "Tuples are immutable sequences created with parentheses. Like lists but cannot be changed.",
    hint: "my_tuple = (1, 2, 3, 4, 5); print(my_tuple[2])",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced protecting information that belongs together. Some data shouldn't be split or modified piece by piece—it stays intact or it's wrong."
    }
  },
  {
    id: 22,
    title: "Dictionaries (Basics)",
    description: "Create a dictionary with keys 'name'='Alice', 'age'=25, 'city'='NYC'. Print the name.",
    starterCode: "",
    tests: [
      { input: "", expected: "Alice" }
    ],
    explanation: "Dictionaries store key-value pairs. Access values with dict[key].",
    hint: "person = {'name': 'Alice', 'age': 25, 'city': 'NYC'}; print(person['name'])",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced direct lookup instead of searching. When information has natural labels, you access it by name, not by position—faster and clearer."
    }
  },
  {
    id: 23,
    title: "Dictionary Methods",
    description: "Start with {'a': 1, 'b': 2}. Add 'c': 3, then print all keys.",
    starterCode: "data = {'a': 1, 'b': 2}\n",
    tests: [
      { input: "", expected: "dict_keys(['a', 'b', 'c'])" }
    ],
    explanation: "Dictionary methods: .keys() gets keys, .values() gets values, .items() gets pairs.",
    hint: "data['c'] = 3; print(data.keys())",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced updating labeled information as circumstances change. Real data isn't static—it grows, shifts, and needs maintenance."
    }
  },
  {
    id: 24,
    title: "Sets",
    description: "Create a set from [1, 2, 2, 3, 3, 3, 4] and print it (duplicates removed).",
    starterCode: "",
    tests: [
      { input: "", expected: /{1, 2, 3, 4}|{[1-4], [1-4], [1-4], [1-4]}/ }
    ],
    explanation: "Sets are unordered collections of unique elements. Automatically remove duplicates.",
    hint: "my_set = set([1, 2, 2, 3, 3, 3, 4]); print(my_set)",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced recognizing when duplicates don't matter. Sometimes you only care what exists, not how many times—this is how you cut through noise."
    }
  },
  {
    id: 25,
    title: "Choosing the Right Collection",
    description: "Store unique student IDs [101, 102, 103, 102, 101] using the right collection type, then print count.",
    starterCode: "",
    tests: [
      { input: "", expected: "3" }
    ],
    explanation: "Choose lists for ordered data, tuples for immutable data, dicts for key-value, sets for unique items.",
    hint: "student_ids = set([101, 102, 103, 102, 101]); print(len(student_ids))",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced choosing the right container for the job. Different tools suit different needs—using the wrong one wastes time or causes errors."
    }
  },
  {
    id: 26,
    title: "Defining Functions",
    description: "Define a function greet(name) that prints 'Hello, [name]!'. Call it with 'Sifiso'.",
    starterCode: "",
    tests: [
      { input: "", expected: "Hello, Sifiso!" }
    ],
    explanation: "Functions are reusable code blocks defined with def. They can accept parameters.",
    hint: "def greet(name): print(f'Hello, {name}!'); greet('Sifiso')",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced creating repeatable procedures. When you do something more than once, naming it and reusing it saves effort and prevents mistakes."
    }
  },
  {
    id: 27,
    title: "Function Parameters",
    description: "Define multiply(a, b) that returns the product. Print multiply(6, 7).",
    starterCode: "",
    tests: [
      { input: "", expected: "42" }
    ],
    explanation: "Functions can accept multiple parameters and use them in calculations.",
    hint: "def multiply(a, b): return a * b; print(multiply(6, 7))",
    pillar: {
      primary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced making processes flexible by allowing different inputs. The same logic works in many situations if you let it adapt to what it receives."
    }
  },
  {
    id: 28,
    title: "Return Values",
    description: "Define is_even(n) that returns True if n is even, False otherwise. Print is_even(10) and is_even(7).",
    starterCode: "",
    tests: [
      { input: "", expected: "True\nFalse" }
    ],
    explanation: "Return values send data back from functions to the caller.",
    hint: "def is_even(n): return n % 2 == 0; print(is_even(10)); print(is_even(7))",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced getting answers back from procedures so they can connect and build on each other. Systems grow by combining smaller, reliable pieces."
    }
  },
  {
    id: 29,
    title: "Variable Scope",
    description: "Create global variable x=10. Define func() that creates local x=20 and prints it, then print global x.",
    starterCode: "x = 10\n",
    tests: [
      { input: "", expected: "20\n10" }
    ],
    explanation: "Variables inside functions are local (separate from globals). Use 'global' keyword to modify global variables.",
    hint: "def func(): x = 20; print(x); func(); print(x)",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced understanding what information is private and what is shared. Keeping things separate prevents interference; sharing what's needed enables cooperation."
    }
  },
  {
    id: 30,
    title: "Built-in Functions",
    description: "Use len(), max(), and min() on [3, 7, 2, 9, 1] and print: count, maximum, minimum.",
    starterCode: "numbers = [3, 7, 2, 9, 1]\n",
    tests: [
      { input: "", expected: "5\n9\n1" }
    ],
    explanation: "Python has many built-in functions: len() for length, max() for maximum, min() for minimum, sum(), etc.",
    hint: "print(len(numbers)); print(max(numbers)); print(min(numbers))",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced using tools that already exist instead of building from scratch. Someone solved this before—borrow their work and focus your effort elsewhere."
    }
  },
  {
    id: 31,
    title: "String Methods",
    description: "Take 'hello world', capitalize each word, and replace 'World' with 'Python'. Print result.",
    starterCode: "text = 'hello world'\n",
    tests: [
      { input: "", expected: "Hello Python" }
    ],
    explanation: "String methods: .title() capitalizes words, .upper()/.lower() change case, .replace() substitutes text.",
    hint: "print(text.title().replace('World', 'Python'))",
    pillar: {
      primary: 'digital'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced systematic text manipulation. Messy data becomes usable when you clean, standardize, and transform it with consistent rules."
    }
  },
  {
    id: 32,
    title: "Modules and Imports",
    description: "Import the math module and print the square root of 144.",
    starterCode: "",
    tests: [
      { input: "", expected: "12.0" }
    ],
    explanation: "Modules are libraries of reusable code. Import with 'import module_name', access with module.function().",
    hint: "import math; print(math.sqrt(144))",
    pillar: {
      primary: 'digital',
      secondary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced borrowing code instead of writing everything yourself. Reusing what others built lets you focus on your specific problem, not reinventing the basics."
    }
  },
  {
    id: 33,
    title: "Reading Files",
    description: "Create a file 'test.txt' with 'Hello' then read and print its contents.",
    starterCode: "",
    tests: [
      { input: "", expected: "Hello" }
    ],
    explanation: "Read files with open(filename, 'r'). Use .read() to get contents. Always close files or use 'with'.",
    hint: "with open('test.txt', 'w') as f: f.write('Hello')\nwith open('test.txt', 'r') as f: print(f.read())",
    pillar: {
      primary: 'digital',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced retrieving information saved earlier. Systems remember so you don't have to—data persists across time, sessions, and shutdowns."
    }
  },
  {
    id: 34,
    title: "Writing Files",
    description: "Write 'Python is awesome!' to 'output.txt', then read and print it.",
    starterCode: "",
    tests: [
      { input: "", expected: "Python is awesome!" }
    ],
    explanation: "Write files with open(filename, 'w'). Use .write() to add content. 'w' overwrites, 'a' appends.",
    hint: "with open('output.txt', 'w') as f: f.write('Python is awesome!')\nwith open('output.txt', 'r') as f: print(f.read())",
    pillar: {
      primary: 'digital',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced saving information permanently for later use. Without persistence, everything disappears when you stop—records ensure continuity."
    }
  },
  {
    id: 35,
    title: "Working with CSV",
    description: "Create a CSV string 'name,age\\nAlice,25\\nBob,30', split by newlines, and print the second row.",
    starterCode: "csv_data = 'name,age\\nAlice,25\\nBob,30'\n",
    tests: [
      { input: "", expected: "Bob,30" }
    ],
    explanation: "CSV (Comma-Separated Values) is a common data format. Use split() to parse simple CSV data.",
    hint: "rows = csv_data.split('\\n'); print(rows[2])",
    pillar: {
      primary: 'digital',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'digital',
      text: "You practiced working with table-structured information. Rows and columns organize data so it's readable by humans and processable by machines."
    }
  },
  {
    id: 36,
    title: "Exception Basics (Try/Except)",
    description: "Try to convert 'abc' to int and catch the ValueError. Print 'Invalid number'.",
    starterCode: "",
    tests: [
      { input: "", expected: "Invalid number" }
    ],
    explanation: "Try/except blocks handle errors gracefully. Code in 'try' runs; if it fails, 'except' catches the error.",
    hint: "try: int('abc')\nexcept ValueError: print('Invalid number')",
    pillar: {
      primary: 'problem-solving',
      secondary: 'digital'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced anticipating failure and responding gracefully. Systems break, inputs fail, networks drop—planning for problems prevents crashes and confusion."
    }
  },
  {
    id: 37,
    title: "Specific Exceptions",
    description: "Ask for a number input, divide 100 by it. Catch ValueError (invalid input) and ZeroDivisionError separately.",
    starterCode: "",
    tests: [
      { input: "abc", prompt: "Enter number: ", expected: "Invalid input" },
      { input: "0", prompt: "Enter number: ", expected: "Cannot divide by zero" }
    ],
    explanation: "Catch specific exceptions with multiple except blocks. Order matters: specific before general.",
    hint: "try: num = int(input('Enter number: ')); print(100 / num)\nexcept ValueError: print('Invalid input')\nexcept ZeroDivisionError: print('Cannot divide by zero')",
    pillar: {
      primary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced responding to different problems differently. Not all errors are the same—the fix depends on understanding what actually went wrong."
    }
  },
  {
    id: 38,
    title: "Debugging Strategies",
    description: "Find and fix the bug: Calculate average of [10, 20, 30]. Code has division by wrong value.",
    starterCode: "numbers = [10, 20, 30]\ntotal = sum(numbers)\naverage = total / 2  # Bug here!\nprint(int(average))",
    tests: [
      { input: "", expected: "20" }
    ],
    explanation: "Debugging involves finding and fixing errors. Common strategies: print statements, reading error messages, checking logic.",
    hint: "Should divide by len(numbers), not 2",
    pillar: {
      primary: 'problem-solving'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced tracing problems back to their source. Systematic investigation finds root causes; guessing wastes time and creates new errors."
    }
  },
  {
    id: 39,
    title: "Final Project Part 1",
    description: "Build a simple contact book: Create dict with 2 contacts (name as key, phone as value), print all names.",
    starterCode: "# Create your contact book\n",
    tests: [
      { input: "", expected: /dict_keys\(\[.*\]\)|.*,.*/ }
    ],
    explanation: "Part 1: Design data structures. A contact book uses dictionaries to store name-phone pairs.",
    hint: "contacts = {'Alice': '555-1234', 'Bob': '555-5678'}; print(contacts.keys())",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced breaking a complete system into manageable parts before building it. Planning structure first prevents rework and clarifies what actually needs to happen."
    }
  },
  {
    id: 40,
    title: "Final Project Part 2",
    description: "Extend contact book: Define add_contact(name, phone) function, add a contact, print total count.",
    starterCode: "contacts = {'Alice': '555-1234'}\n# Define and use add_contact function\n",
    tests: [
      { input: "", expected: "2" }
    ],
    explanation: "Part 2: Add functionality with functions. This combines dictionaries, functions, and data manipulation.",
    hint: "def add_contact(name, phone): contacts[name] = phone; add_contact('Bob', '555-5678'); print(len(contacts))",
    pillar: {
      primary: 'problem-solving',
      secondary: 'economic'
    },
    lensCallout: {
      pillar: 'problem-solving',
      text: "You practiced verifying that all pieces work together, not just individually. Integration reveals gaps that isolated testing misses—only the full flow confirms correctness."
    }
  },
  {
    id: 41,
    title: "Understanding AI Outputs",
    description: "AI Literacy: Why machines sound confident when they're wrong. No code—just understanding.",
    starterCode: "# This is a reflection-only module.\n# Read the content, reflect on the questions.\n# No code to write here.\n",
    tests: [],
    explanation: "This module builds AI Literacy: understanding that confidence ≠ correctness. AI systems predict patterns, they don't know truth. You are responsible for verifying AI outputs.",
    hint: "Read carefully. Reflect deeply. This knowledge protects you.",
    pillar: {
      primary: 'ai'
    },
    lensCallout: {
      pillar: 'ai',
      text: "You practiced distinguishing confidence from correctness. AI systems generate text that sounds authoritative without verifying facts—human judgment remains essential."
    }
  },
];

// Add learning content to all levels
export const levels = addLearningContentToLevels(baseLevels);